import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useQueries,
} from "@tanstack/react-query";
import {
  getClientsWithDraftOrders,
  getClientsWithPendingMandates,
  getDistributorBankAccountDetailsApi,
  getDistributorProfile,
  createDistributorBankAccountDetailsApi,
  updateDistributorBankAccountDetailsApi,
  getDistributorNomineeDetailsApi,
  updateDistributorProfile,
  upsertDistributorNomineeDetailsApi,
  getBrokerageCard,
  getDistributorInfoByHandle,
  getDistributorPreferencesApi,
  updateDistributorPreferencesApi,
} from "../../api/distributor/distributor";

export function useUpdateDistributorProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDistributorProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distributor"] });
    },
  });
}

export function useClientsWithPendingMandates({
  page = 1,
  pageSize = 50,
}: {
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ["clients-with-pending-mandates", page, pageSize],
    queryFn: () => getClientsWithPendingMandates({ page, pageSize }),
  });
}

export function useClientsWithDraftOrders({
  page = 1,
  pageSize = 50,
}: {
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ["clients-with-draft-orders", page, pageSize],
    queryFn: () => getClientsWithDraftOrders({ page, pageSize }),
  });
}

export const getDistributorProfileOptions = (enabled?: boolean) =>
  queryOptions({
    queryKey: ["distributor"],
    queryFn: () => getDistributorProfile(),
    enabled,
  });

export const useDistributorProfile = (enabled = true) => {
  return useQuery(getDistributorProfileOptions(enabled));
};

export const getDistributorByHandleOptions = (
  variables: { handle: string },
  options = { enabled: true }
) =>
  queryOptions({
    queryKey: ["distributor/by-handle", variables.handle],
    queryFn: () => getDistributorInfoByHandle(variables.handle),
    ...options,
  });

export const useDistributorInfoByHandle = (
  variables: { handle: string },
  options = { enabled: true }
) => {
  return useQuery(getDistributorByHandleOptions(variables, options));
};

export const getDistributorBankDetailsOptions = () =>
  queryOptions({
    queryKey: ["distributor/bank-details"],
    queryFn: getDistributorBankAccountDetailsApi,
    retry: 0,
  });

export const useDistributorBanksDetails = () => {
  return useQuery(getDistributorBankDetailsOptions());
};

export const useCreateDistributorBankDetails = () => {
  const queryClient = useQueryClient();

  const queryResponse = useMutation({
    mutationFn: createDistributorBankAccountDetailsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["distributor/bank-details"],
      });
    },
  });

  return queryResponse;
};

export const useUpdateDistributorBankDetails = () => {
  const queryClient = useQueryClient();

  const queryResponse = useMutation({
    mutationFn: updateDistributorBankAccountDetailsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["distributor/bank-details"],
      });
    },
  });

  return queryResponse;
};

export const getDistributorNomineeDetailsOptions = () =>
  queryOptions({
    queryKey: ["distributor/nominee-details"],
    queryFn: getDistributorNomineeDetailsApi,
    retry: 0,
  });
export const useDistributorNomineeDetails = () => {
  return useQuery(getDistributorNomineeDetailsOptions());
};

export const useUpsertDistributorNominee = () => {
  const queryClient = useQueryClient();
  const queryResponse = useMutation({
    mutationFn: upsertDistributorNomineeDetailsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["distributor/nominee-details"],
      });
    },
  });
  return queryResponse;
};

export const getDistributorPreferencesOptions = () =>
  queryOptions({
    queryKey: ["distributor/preferences"],
    queryFn: getDistributorPreferencesApi,
  });

export const useDistributorPreferences = () => {
  return useQuery(getDistributorPreferencesOptions());
};

export const useUpdateDistributorPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDistributorPreferencesApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["distributor/preferences"],
      });
      queryClient.invalidateQueries({
        queryKey: ["funds/list"],
      });
    },
  });
};

export const useDistributorOnboarding = () => {
  const queryResponseResult = useQueries({
    queries: [
      getDistributorBankDetailsOptions(),
      getDistributorNomineeDetailsOptions(),
    ],
  });

  return queryResponseResult;
};

export const useBrokerageCard = (
  page: number,
  pageSize: number,
  search: string,
  amcFilter: string[],
  productTypeFilter: string[] = [],
  sort?: Array<{ id: string; desc: boolean }>
) => {
  return useQuery({
    queryKey: [
      "brokerage-card",
      page,
      pageSize,
      search,
      amcFilter,
      productTypeFilter,
      sort,
    ],
    queryFn: () =>
      getBrokerageCard(
        page,
        pageSize,
        search,
        amcFilter,
        productTypeFilter,
        sort
      ),
    placeholderData: (previousData) => previousData,
  });
};
